import { useQuery } from "@tanstack/react-query";
import { THEMES, useTheme, type Theme } from "@/lib/theme";
import { ReaderScreen } from "@/features/reader/ReaderScreen";
import { UploadScreen } from "@/features/admin/UploadScreen";
import { TranslationEditor } from "@/features/editor/TranslationEditor";
import { ReviewQueueScreen } from "@/features/review/ReviewQueueScreen";
import { LearnScreen } from "@/features/learn/LearnScreen";
import { HomeScreen } from "@/features/home/HomeScreen";
import { GlossaryScreen } from "@/features/glossary/GlossaryScreen";
import { AdminConsole } from "@/features/admin-console/AdminConsole";
import { SettingsScreen } from "@/features/settings/SettingsScreen";
import { LibraryScreen } from "@/features/library/LibraryScreen";
import { ChatScreen } from "@/features/chat/ChatScreen";
import { PrepareScreen } from "@/features/prepare/PrepareScreen";
import { useUrlParam } from "@/features/reader/useUrlState";
import { AppBar } from "@/components/app-bar";
import { SiteFooter } from "@/components/site-footer";

/**
 * Phase 1 walking-skeleton screen.
 *
 * It exists to prove four things end to end before any feature is built:
 * the Mimbar tokens load, all five scripts render in their own family and
 * direction, every theme repaints without a reload, and the client can reach
 * the Spring Boot server. The khutbah content below is sample text, labelled as
 * such — no real translation is shown, because none has been produced yet.
 */

const THEME_LABELS: Record<Theme, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
  paper: "Paper",
  "high-contrast": "High contrast",
};

function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="inline-flex flex-wrap gap-1 rounded-lg border border-border bg-card p-1"
    >
      {THEMES.map((option) => {
        const selected = option === theme;
        return (
          <button
            key={option}
            role="radio"
            aria-checked={selected}
            onClick={() => setTheme(option)}
            style={{ transitionDuration: "var(--duration-base)" }}
            className={[
              "min-h-11 rounded-md px-3 text-sm transition-colors",
              selected
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            ].join(" ")}
          >
            {THEME_LABELS[option]}
          </button>
        );
      })}
    </div>
  );
}

/** One source badge and one translation badge per khutbah — never two of a kind. */
function ProvenanceBadge({
  kind,
  children,
}: {
  kind: "publisher" | "official" | "reviewed" | "ai" | "user" | "stale";
  children: string;
}) {
  const styles: Record<typeof kind, string> = {
    publisher: "bg-status-publisher-bg text-status-publisher",
    official: "bg-status-official text-status-official-foreground",
    reviewed: "bg-status-reviewed-bg text-status-reviewed",
    ai: "bg-status-ai-bg text-status-ai",
    user: "bg-status-user-bg text-status-user",
    stale: "bg-status-stale-bg text-status-stale",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs ${styles[kind]}`}
    >
      {children}
    </span>
  );
}

type Health = { status: string };

/**
 * Validates at the trust boundary. A type assertion on a fetch response is a
 * lie that compiles: TypeScript checks nothing at runtime, so an unexpected
 * shape would surface later as an undefined read rather than here.
 */
function parseHealth(value: unknown): Health {
  if (
    typeof value !== "object" ||
    value === null ||
    typeof (value as { status?: unknown }).status !== "string"
  ) {
    throw new Error("unexpected health response shape");
  }
  return { status: (value as Health).status };
}

function ServerStatus() {
  const { data, isPending, isError } = useQuery({
    queryKey: ["health"],
    // The signal is passed through, so an unmounted or superseded request is
    // cancelled rather than merely ignored when it answers.
    queryFn: async ({ signal }) => {
      const response = await fetch("/actuator/health", { signal });
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      return parseHealth(await response.json());
    },
    retry: false,
  });

  if (isPending) {
    return <span className="text-sm text-muted-foreground">Checking the server…</span>;
  }
  if (isError) {
    return (
      <span className="text-sm text-muted-foreground">
        Server not reachable. Start it with the worker and app profiles, then reload.
      </span>
    );
  }
  return (
    <span className="text-sm text-muted-foreground">
      Server reachable — status {data.status}.
    </span>
  );
}

const SPECIMENS = [
  { lang: "ar", script: "script-ar", name: "Arabic", text: "الْحَمْدُ لِلَّهِ رَبِّ الْعَالَمِينَ" },
  { lang: "ur", script: "script-ur", name: "Urdu", text: "تمام تعریفیں اللہ کے لیے ہیں" },
  { lang: "ml", script: "script-ml", name: "Malayalam", text: "സർവ്വസ്തുതിയും അല്ലാഹുവിന്" },
  { lang: "kn", script: "script-kn", name: "Kannada", text: "ಸರ್ವಸ್ತುತಿಯು ಅಲ್ಲಾಹನಿಗೆ" },
  { lang: "ta", script: "script-ta", name: "Tamil", text: "எல்லாப் புகழும் அல்லாஹ்வுக்கே" },
] as const;

/**
 * The app shell: a bar above, a footer below, and the screen between them.
 *
 * Both live here rather than in each screen because they are the product's
 * only global chrome — twelve screens rendering their own copy is twelve
 * chances for them to disagree.
 *
 * <h2>Why the footer is here and not inside a screen</h2>
 *
 * It was inside each screen's scrolling pane, which made it a sibling of the
 * content and a neighbour of the icon rail: it began 56px in, stopped where
 * the pane stopped, and on some screens overhung a capped column by 500px.
 * The product owner's call is quran.com's shape — a band across the whole
 * window, under the rail as well as the content, belonging to neither.
 *
 * So the scroller holds two things: the screen, and the footer beneath it.
 * The screen is told to be at least a full pane tall, so a short page still
 * fills the window and the footer sits at the bottom of it rather than
 * halfway up.
 *
 * <h2>Why this does not break the reader</h2>
 *
 * The reader keeps its fixed chrome — rail, toolbar and chat stay put while
 * the khutbah scrolls in its own pane. That pane is nested inside this
 * scroller, and browsers chain the two: scrolling past the end of the
 * khutbah carries on into the outer scroller and brings the footer up. The
 * footer is reachable without ever being in the way.
 *
 * `min-h-0` on the flex children is what lets an inner pane scroll at all —
 * without it a flex child grows to its content and the overflow lands on the
 * shell instead of the pane.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex h-dvh flex-col overflow-hidden bg-background text-foreground"
      // Published so a screen that sizes itself to the viewport can subtract
      // the bar without hard-coding its height in six places. AppBar is h-12.
      style={{ '--app-bar-height': '3rem' } as React.CSSProperties}
    >
      <AppBar />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/*
          The slot GROWS with its screen; it does not pin to one viewport.

          It was `h-full`, which fixed a real bug — a screen that manages its
          own scrolling needs a parent with a definite height or its own
          `h-full` falls back to auto — but it created another: a screen that
          scrolls as a page is taller than one viewport, overflowed this box
          visibly, and the footer, sitting after the box in normal flow,
          ended up drawn over by the content it was supposed to follow. The
          learning screen showed it plainly.

          `min-h-full` keeps a short page filling the window and lets a long
          one push the footer down where it belongs. The screens that manage
          their own scrolling no longer depend on this box at all: they size
          themselves from the viewport minus the bar, so their panes keep
          scrolling and this scroller still has only the footer to give.
        */}
        <div className="min-h-full">{children}</div>
        <SiteFooter />
      </div>
    </div>
  );
}

export default function App() {
  // The selected khutbah lives in the URL so a reading is a shareable link.
  // Empty means nothing is selected, and the checks below stand in.
  const [khutbahId] = useUrlParam("khutbah", "");
  // Which screen, also in the URL. Still no router: two parameters do not
  // justify one, and introducing it now would fix the routing shape before
  // the screens that have to live in it exist.
  const [screen] = useUrlParam("screen", "");

  // The editor surface. Reached deliberately, because it is behind HTTP Basic
  // and the authentication mechanism is still an open question (brief §18).
  if (screen === "upload") {
    return <Shell><UploadScreen /></Shell>;
  }

  // Entering a translation needs a khutbah; without one there is nothing to
  // translate, so it falls through to the picker below rather than rendering
  // an editor with no passages.
  if (screen === "translate" && khutbahId) {
    return <Shell><TranslationEditor khutbahId={khutbahId} /></Shell>;
  }

  if (screen === "review") {
    return <Shell><ReviewQueueScreen /></Shell>;
  }

  if (screen === "learn" && khutbahId) {
    return <Shell><LearnScreen khutbahId={khutbahId} /></Shell>;
  }

  if (screen === "home") {
    return <Shell><HomeScreen /></Shell>;
  }

  if (screen === "glossary") {
    return <Shell><GlossaryScreen /></Shell>;
  }

  if (screen === "admin") {
    return <Shell><AdminConsole /></Shell>;
  }

  if (screen === "settings") {
    return <Shell><SettingsScreen /></Shell>;
  }

  if (screen === "library") {
    return <Shell><LibraryScreen /></Shell>;
  }

  // Preparation: searching the published corpus by word. Before the khutbah
  // branch for the same reason the chat screen is -- it carries ?topic=, and
  // a result's link is what sets ?khutbah=.
  if (screen === "prepare") {
    return <Shell><PrepareScreen /></Shell>;
  }

  // Before the khutbah branch below, which would otherwise claim the URL:
  // the chat screen carries ?khutbah= as its chosen scope, not as a reading.
  if (screen === "chat") {
    return <Shell><ChatScreen /></Shell>;
  }

  // Nothing asked for: Home, not the walking skeleton. The skeleton was the
  // landing page while there was nothing to land on; there are now eleven
  // screens, and showing a diagnostic page instead of the product made all
  // of them invisible to anyone who did not know the query parameters.
  if (screen === "" && !khutbahId) {
    return <Shell><HomeScreen /></Shell>;
  }

  // The reader owns the whole viewport: it has its own icon rail, outline and
  // chat dock, and nesting that inside the skeleton's centred column would
  // squeeze the reading measure the design system sizes so carefully.
  if (khutbahId) {
    return (
      <Shell>
        <ReaderScreen khutbahId={khutbahId} />
      </Shell>
    );
  }

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 p-4">
          <div>
            {/* The lockup at 28px, per the brand book. Two inks: the on-dark
                lockup swaps the steps to the Dark theme's lighter teal, so it
                is a file swap rather than a filter. */}
            <h1>
              <img
                src="/brand/lockup.svg"
                alt="Almimbar"
                height={28}
                className="h-7 w-auto dark:hidden"
              />
              <img
                src="/brand/lockup-on-dark.svg"
                alt="Almimbar"
                height={28}
                className="hidden h-7 w-auto dark:block"
              />
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">Understand the khutbah</p>
          </div>
          <ThemeSwitcher />
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 p-4 pb-16">
        <WalkingSkeleton />

        <footer className="border-t border-border pt-4 text-xs text-muted-foreground">
          Unofficial reading aid. Answers come only from this khutbah's text —
          check the original before relying on it.
        </footer>
      </main>
    </div>
  );
}

/**
 * The pre-content state, and the checks it was built for.
 *
 * Shown when no khutbah is selected. The Arabic below is sample text and is
 * labelled as such: it verifies that the tokens load, that all five scripts
 * render in their own family and direction, and that the server is reachable.
 * It is deliberately NOT presented as content the system produced.
 */
function WalkingSkeleton() {
  return (
    <>
        <section className="rounded-lg border border-border bg-muted p-4">
          <h2 className="text-sm font-medium">Walking skeleton</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            This screen checks that the design tokens, the five scripts, the
            themes and the API connection all work. The khutbah text below is
            sample content for that check, not a translation the system produced.
          </p>
          <div className="mt-3">
            <ServerStatus />
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">This Friday</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Friday 23 October 2026 · ≈ 12 Jumada al-Awwal 1448
          </p>

          <article className="mt-4 rounded-lg border border-border bg-card p-4 shadow-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span
                aria-hidden="true"
                className="inline-block size-2 rounded-full bg-institution-oman"
              />
              Oman Awqaf
            </div>

            <h3 className="script-ar mt-2 text-2xl" lang="ar">
              تَقْوَى اللهِ وَصِدْقُ الْقَوْلِ
            </h3>
            <p className="mt-1 text-lg">Mindfulness of Allah and truthful speech</p>

            <div className="mt-3 flex flex-wrap gap-2">
              <ProvenanceBadge kind="publisher">Publisher-sourced</ProvenanceBadge>
              <ProvenanceBadge kind="ai">AI translation · unreviewed</ProvenanceBadge>
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              Unofficial reading aid · not endorsed by the publisher · Source:{" "}
              <a
                href="https://www.mara.gov.om/jumaa/"
                className="text-primary underline"
                rel="noreferrer noopener"
                target="_blank"
              >
                mara.gov.om/jumaa
              </a>{" "}
              · fetched 21 Oct 2026
            </p>
          </article>
        </section>

        <section className="reader">
          <h2 className="text-2xl font-semibold">First khutbah · page 1</h2>

          {/* Quran block: framed, tashkeel intact, never bolded or recoloured,
              and the reference stays "detected by matching" until confirmed. */}
          <figure className="mt-4 rounded-lg border border-quote-arabic-border bg-quote-arabic-bg p-4">
            <p className="script-ar" lang="ar">
              يَا أَيُّهَا الَّذِينَ آمَنُوا اتَّقُوا اللَّهَ وَقُولُوا قَوْلًا سَدِيدًا
            </p>
            <figcaption className="mt-3 text-xs text-muted-foreground">
              Al-Ahzab 33:70 · detected by matching
            </figcaption>
          </figure>

          <div className="mt-2 rounded-lg bg-translation-bg p-4">
            <p className="script-ml" lang="ml">
              വിശ്വസിച്ചവരേ, അല്ലാഹുവെ സൂക്ഷിക്കുക, സത്യസന്ധമായ വാക്ക് പറയുക.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Translation of meaning · sample text
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">Script check</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Each row sets its own family, direction and leading from one class.
          </p>
          <ul className="mt-4 divide-y divide-border rounded-lg border border-border bg-card">
            {SPECIMENS.map((specimen) => (
              <li
                key={specimen.lang}
                className="flex flex-col gap-1 p-4 sm:flex-row sm:items-baseline sm:gap-6"
              >
                <span className="w-24 shrink-0 text-xs text-muted-foreground">
                  {specimen.name}
                </span>
                <span className={`${specimen.script} text-lg`} lang={specimen.lang}>
                  {specimen.text}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-lg border border-dashed border-border p-4">
          <h2 className="text-sm font-medium">Open a khutbah</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Add <code className="rounded bg-muted px-1">?khutbah=&lt;id&gt;</code>{" "}
            to the address to read one. Published khutbahs are readable without
            an account; a draft needs the editor surface.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Nothing to read yet?{" "}
            <a href="?screen=upload" className="text-primary underline">
              Upload a khutbah
            </a>{" "}
            — the editor surface asks for credentials.
          </p>
        </section>
    </>
  );
}
